import { compact } from 'util/array'
import { isUndefinedOrNull } from 'util/checks'
import { LabelMap } from 'util/fields-labels/model-fieldsLabels'
import { getFieldLabel } from 'util/fields-labels/util-fieldsLabels'
import { MetaPath, MetaRoot } from 'util/metaPath'
import { isObjectDeepEmpty } from 'util/object'

import { getFieldLabelConfig } from './getFieldLabelConfig'
import {
  FieldsSummaryErrorObject,
  ValidationErrorModalConfig,
  ValidationErrorModalItemModel,
} from './model-validationErrorsModal'

const DEFAULT_ERROR_MESSAGE = 'Existem campos preenchidos de forma incorreta.'

export function convertErrorsToModalItems<FormModel>(
  errors: FieldsSummaryErrorObject<FormModel> | FieldsSummaryErrorObject<FormModel>[],
  labels: LabelMap<FormModel>,
  config: ValidationErrorModalConfig<FormModel>,
  metaRoot: MetaRoot<FormModel>,
  parentName: MetaPath<any> | null = null
): ValidationErrorModalItemModel[] {
  const items = Array.isArray(errors)
    ? errors.flatMap((errorItem: FieldsSummaryErrorObject<FormModel>, index: number) => {
        return buildError<FormModel>(errorItem, labels, config, metaRoot, parentName, index + 1)
      })
    : buildError<FormModel>(errors, labels, config, metaRoot, parentName)

  return errors && compact(items)
}

function buildError<FormModel>(
  errors: FieldsSummaryErrorObject<FormModel>,
  labels: LabelMap<FormModel>,
  config: ValidationErrorModalConfig<FormModel>,
  metaRoot: MetaRoot<FormModel>,
  parentName: MetaPath<any> | null = null,
  indexPrefixError: number | null = null
): ValidationErrorModalItemModel[] {
  if (typeof errors === 'string') {
    const { label, titleColor, showLabelOnModal, extractedError } = getInfosFromErrorObject<FormModel>(
      null,
      errors,
      labels,
      config,
      metaRoot,
      parentName,
      indexPrefixError
    )
    return [{ label, titleColor, showLabelOnModal, error: extractedError as string, fields: [] }]
  }

  return Object.entries(errors).map(([key, error]: [string, string | object]) => {
    if (!isObjectDeepEmpty(error)) {
      const { name, label, titleColor, showLabelOnModal, showChildren, extractedError } = getInfosFromErrorObject<
        FormModel
      >(key, error, labels, config, metaRoot, parentName, indexPrefixError)

      if (typeof extractedError === 'object') {
        const fields = showChildren ? convertErrorsToModalItems(error, labels, config, name) : []

        return {
          label,
          titleColor,
          showLabelOnModal,
          fields,
          error: showChildren ? null : DEFAULT_ERROR_MESSAGE,
        }
      }

      return { label, titleColor, showLabelOnModal, error: extractedError, fields: [] }
    }

    return null
  })
}

function getInfosFromErrorObject<FormModel>(
  key: string | null,
  error: string | object,
  labels: LabelMap<FormModel>,
  config: ValidationErrorModalConfig<FormModel>,
  metaRoot: MetaRoot<FormModel>,
  parentName: MetaPath<any> | null = null,
  indexPrefixError: number | null = null
) {
  const formName = parentName ? parentName[key] : metaRoot[key]
  const name = key === 'formError' ? key : formName
  const fieldLabel = getFieldLabel(name, labels)
  const label = `${indexPrefixError !== null ? indexPrefixError + '. ' : ''}${fieldLabel}`
  const { titleColor, hideChildrenOnModal, hideLabelOnModal, extractError } = getFieldLabelConfig(name, config)

  const showLabelOnModal =
    !(typeof hideLabelOnModal === 'function' ? hideLabelOnModal(error) : hideLabelOnModal) &&
    !isUndefinedOrNull(fieldLabel)
  const showChildren = !(typeof hideChildrenOnModal === 'function' ? hideChildrenOnModal(error) : hideChildrenOnModal)
  const extractedError = extractError?.(error) ?? error
  const computedError =
    !showLabelOnModal && typeof extractedError === 'string' && !isUndefinedOrNull(indexPrefixError)
      ? `${indexPrefixError + '. '}${extractedError}`
      : extractedError

  return {
    name,
    label,
    titleColor,
    showLabelOnModal,
    showChildren,
    extractedError: computedError,
  }
}
